import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BoldBiTabService } from '../../service/bold-bi-tab.service';
import { UserService } from '../../service/user.service';
import { TranslateModule } from '@ngx-translate/core';

declare const BoldBI: any;
/**
 * Displays an embedded Bold BI dashboard tab by reading the dashboard ID from the route query
 * parameters and the embed server URL from session storage, then loading the Bold BI script and
 * rendering the dashboard into the component container.
 * @component
 * @selector app-dashboard-tab
 * @example
 *   <app-dashboard-tab></app-dashboard-tab>
 * @input none
 * @output none
 * @method ngOnInit - Retrieves the auth token, subscribes to route query parameters, validates required
 *   values, loads the Bold BI script, and initializes the dashboard embed.
 * @method ngOnDestroy - Lifecycle hook reserved for cleanup when the component is destroyed.
 * @property isLoading - Indicates whether the dashboard is currently being loaded.
 */
@Component({
  selector: 'app-dashboard-tab',
  imports: [CommonModule, TranslateModule],
  templateUrl: './dashboard-tab.component.html',
  styleUrl: './dashboard-tab.component.css'
})
export class DashboardTabComponent {
  isLoading = true;
  private dashboardId = '';
  private serverUrl = '';
  private activeRequestId = 0;
  private currentEmbedInstance: any = null;

  constructor(private route: ActivatedRoute, private boldBiTab: BoldBiTabService, private user: UserService) { }

  /**
   * Initializes the dashboard tab by reading auth and query parameters, then loading the Bold BI tab script and dashboard.
   * @example
   * gOnInit()
   * void
   * @returns {void} No return value.
   */
  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const dashboardId: string = params['id'] ?? '';
      const serverUrl: string = sessionStorage.getItem('boldBiEmbedUrl') ?? '';
      const requestId = ++this.activeRequestId;

      if (!dashboardId || !serverUrl) {
        console.error('DashboardTab: missing id param or boldBiEmbedUrl');
        this.isLoading = false;
        return;
      }

      this.user.ensureValidToken().subscribe({
        next: (token) => {
          if (requestId !== this.activeRequestId) return;
          this.boldBiTab.loadScript()
            .then(() => {
              if (requestId !== this.activeRequestId) return;
              this.currentEmbedInstance = this.boldBiTab.createAndLoad({
                containerId: 'dashboard_tab_container',
                serverUrl,
                dashboardId,
                token,
                customerId: this.user.getCustomerId(),
              });
              this.isLoading = false;
            })
            .catch(err => {
              if (requestId !== this.activeRequestId) return;
              console.error('BoldBI script load failed in tab:', err);
              this.isLoading = false;
            });
        },
        error: (err) => {
          if (requestId !== this.activeRequestId) return;
          if (err?.message === 'SESSION_EXPIRED') {
            window.location.href = '/emailVerification';
            return;
          }
          console.error('DashboardTab: token refresh failed:', err);
          this.isLoading = false;
        },
      });
    });
  }
  
  ngOnDestroy(): void {
    ++this.activeRequestId;
    this.boldBiTab.destroyInstance(this.currentEmbedInstance);
    this.currentEmbedInstance = null;
  }
}
